import { useState, useMemo } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, message, Space, DatePicker, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { paymentsAPI, invoicesAPI } from '../api/endpoints';
import { formatCurrency, formatDate, getPaymentStatusColor, getPaymentStatusLabel, getPaymentMethodLabel } from '../utils/helpers';
import dayjs from 'dayjs';

const PaymentsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const { data: paymentsData, refetch } = useQuery('payments', () => paymentsAPI.list());
  const { data: allInvoicesData } = useQuery(['invoices', { page_size: 1000 }], () => invoicesAPI.list({ page_size: 1000 }));
  const createMutation = useMutation(paymentsAPI.create);
  const updateMutation = useMutation(({ id, data }) => paymentsAPI.update(id, data));
  const deleteMutation = useMutation(paymentsAPI.delete);

  const payments = paymentsData?.data?.results || [];
  const allInvoices = allInvoicesData?.data?.results || [];

  // Show all invoices that can receive payments (exclude paid and cancelled)
  const invoiceOptions = useMemo(() => {
    const available = allInvoices.filter((inv) => inv.status === 'issued');
    // When editing, ensure the current invoice appears even if it's now paid
    if (editingRecord) {
      const ids = new Set(available.map((inv) => inv.id));
      if (!ids.has(editingRecord.invoice)) {
        const editInvoice = allInvoices.find((inv) => inv.id === editingRecord.invoice);
        if (editInvoice) return [editInvoice, ...available];
      }
    }
    return available;
  }, [editingRecord, allInvoices]);

  const columns = [
    {
      title: 'Factura',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Cliente',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Fecha Pago',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => formatDate(date),
    },
    {
      title: 'Método',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method) => getPaymentMethodLabel(method),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getPaymentStatusColor(status)}>
          {getPaymentStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button type="primary" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  const handleEdit = (record) => {
    setEditingId(record.id);
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      payment_date: dayjs(record.payment_date),
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Eliminar Pago',
      content: '¿Está seguro que desea eliminar este pago?',
      okText: 'Sí',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(id);
          message.success('Pago eliminado correctamente');
          refetch();
        } catch (error) {
          message.error('Error al eliminar pago');
        }
      },
    });
  };

  const handleDownloadCSV = () => {
    const headers = ['Factura', 'Cliente', 'Monto', 'Fecha Pago', 'Método', 'Estado'];
    const rows = payments.map((p) => [
      p.invoice_number,
      p.customer_name,
      p.amount,
      p.payment_date,
      getPaymentMethodLabel(p.payment_method),
      getPaymentStatusLabel(p.status),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pagos_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const onFinish = async (values) => {
    try {
      const data = {
        ...values,
        payment_date: values.payment_date.format('YYYY-MM-DD'),
      };

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data });
        message.success('Pago actualizado correctamente');
      } else {
        await createMutation.mutateAsync(data);
        message.success('Pago registrado correctamente');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingId(null);
      setEditingRecord(null);
      refetch();
    } catch (error) {
      message.error('Error al guardar pago');
    }
  };

  return (
    <div>
      <Card
        title="Gestión de Pagos"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
              Descargar CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(null);
                setEditingRecord(null);
                form.resetFields();
                setIsModalOpen(true);
              }}
            >
              Nuevo Pago
            </Button>
          </Space>
        }
      >
        <Table dataSource={payments} columns={columns} rowKey="id" />
      </Card>

      <Modal
        title={editingId ? 'Editar Pago' : 'Registrar Pago'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsModalOpen(false)}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="invoice"
            label="Factura"
            rules={[{ required: true, message: 'Por favor selecciona una factura' }]}
          >
            <Select placeholder="Selecciona una factura">
              {invoiceOptions.map((invoice) => (
                <Select.Option key={invoice.id} value={invoice.id}>
                  {invoice.invoice_number} - {invoice.customer_name} ({formatCurrency(invoice.remaining_amount)})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Monto del Pago"
            rules={[{ required: true, message: 'Por favor ingresa el monto' }]}
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item
            name="payment_method"
            label="Método de Pago"
            rules={[{ required: true, message: 'Por favor selecciona un método' }]}
          >
            <Select>
              <Select.Option value="bank_transfer">Transferencia Bancaria</Select.Option>
              <Select.Option value="check">Cheque</Select.Option>
              <Select.Option value="credit_card">Tarjeta de Crédito</Select.Option>
              <Select.Option value="cash">Efectivo</Select.Option>
              <Select.Option value="other">Otro</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="payment_date"
            label="Fecha del Pago"
            rules={[{ required: true, message: 'Por favor selecciona una fecha' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="reference" label="Referencia">
            <Input placeholder="Folio, número de comprobante, etc." />
          </Form.Item>

          <Form.Item
            name="status"
            label="Estado"
            initialValue="confirmed"
          >
            <Select>
              <Select.Option value="pending">Pendiente</Select.Option>
              <Select.Option value="confirmed">Confirmado</Select.Option>
              <Select.Option value="rejected">Rechazado</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notas">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentsPage;
